import { useState, useCallback } from "react";
import {
  Grid,
  Card,
  CardContent,
  Box,
  Autocomplete,
  List,
  ListItemButton,
  ListItemText,
  Button as MuiButton,
} from "@mui/material";
import { Alert, Button, TextField, Typography } from "@platform/design-system";
import { Link as RouterLink } from "react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@platform/api";

import { extractErrorMessage } from "../../utils";
import useToastNotificationService from "../../hooks/useToastNotificationService";
import { CENTRO, ONE_DIGITAL, ROUTE_NAMES } from "../../constants";
import { Loader } from "../../components";

interface D365Account {
  accountid?: string;
  name?: string;
  digins_companytype?: number;
}

interface PrepareAddBrokerPartnerOption {
  label: string;
  value: string;
  options: D365Account[];
}

interface AddBrokerPartnerRequest {
  d356BrokerId: string | undefined;
  d356BrokerName: string | undefined;
  type: string | undefined;
}

// Response shape returned by prepareAddBrokerPartner endpoint (normalized via select)
interface PreparedBrokerOptionsSelection {
  oneDigitalOptions: D365Account[];
  centroOptions: D365Account[];
}

// We don't currently consume a response body from addBrokerPartner; use void for mutation result.

const BrokerPreRegistration: React.FC = (): React.ReactElement => {
  const [broker, setBroker] = useState<D365Account | null>(null);
  const [selectedTab, setSelectedTab] = useState<string>(CENTRO);

  const axiosInstance = useApi();
  const queryClient = useQueryClient();
  const { createToastNotification } = useToastNotificationService();

  const prepareQuery = useQuery<
    PrepareAddBrokerPartnerOption[],
    Error,
    PreparedBrokerOptionsSelection
  >({
    queryKey: ["prepareAddBrokerPartner"],
    queryFn: async (): Promise<PrepareAddBrokerPartnerOption[]> => {
      const response = await axiosInstance.post<
        PrepareAddBrokerPartnerOption[]
      >("/brokerportal/v1/prepareAddBrokerPartner", {});
      return response.data;
    },
    select: (data): PreparedBrokerOptionsSelection => ({
      oneDigitalOptions:
        data.find((option) => option.value === ONE_DIGITAL)?.options || [],
      centroOptions:
        data.find((option) => option.value === CENTRO)?.options || [],
    }),
  });

  const { oneDigitalOptions = [], centroOptions = [] } =
    prepareQuery.data ?? {};
  const isLoading: boolean = prepareQuery.isLoading;
  const error: Error | null = prepareQuery.error ?? null;

  const addBrokerMutation = useMutation<void, Error, AddBrokerPartnerRequest>({
    mutationFn: async (body: AddBrokerPartnerRequest): Promise<void> => {
      await axiosInstance.post("/brokerportal/v1/addBrokerPartner", body);
    },
    onSuccess: async (): Promise<void> => {
      setBroker(null);
      createToastNotification({
        severity: "success",
        message: "Broker added successfully!",
      });
      await queryClient.invalidateQueries({
        queryKey: ["prepareManageBrokers"],
      });
    },
    onError: (err: Error): void => {
      createToastNotification({
        severity: "error",
        message: extractErrorMessage(err),
      });
    },
  });

  const handleTabClick = useCallback((tab: string): void => {
    setSelectedTab(tab);
    setBroker(null);
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>): void => {
      e.preventDefault();
      if (!broker) return;
      addBrokerMutation.mutate({
        d356BrokerId: broker.accountid,
        d356BrokerName: broker.name,
        type: selectedTab,
      });
    },
    [broker, addBrokerMutation, selectedTab],
  );

  const currentOptions: D365Account[] =
    selectedTab === CENTRO ? centroOptions : oneDigitalOptions;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {error && (
        <Box mb={2}>
          <Alert severity="error">
            {extractErrorMessage(
              error,
              "Unable to retrieve list of brokers. Try again later",
            )}
          </Alert>
        </Box>
      )}
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <Box mb={4}>
            <Typography variant="display-medium">Add Broker</Typography>
          </Box>

          <Grid container spacing={2}>
            <Grid size={3}>
              <List component="nav" aria-label="main mailbox folders">
                <ListItemButton
                  selected={selectedTab === CENTRO}
                  onClick={() => handleTabClick(CENTRO)}
                >
                  <ListItemText primary="Centro Benefits Research" />
                </ListItemButton>
                <ListItemButton
                  selected={selectedTab === ONE_DIGITAL}
                  onClick={() => handleTabClick(ONE_DIGITAL)}
                >
                  <ListItemText primary="OneDigital LLC" />
                </ListItemButton>
              </List>
            </Grid>
            <Grid size={6}>
              <Card>
                <form onSubmit={handleSubmit}>
                  <CardContent>
                    <Grid container spacing={2} justifyContent="center">
                      <Grid size={12}>
                        <Autocomplete
                          value={broker}
                          onChange={(_, _broker: D365Account | null) =>
                            setBroker(_broker)
                          }
                          options={currentOptions}
                          getOptionLabel={(option) => option.name || ""}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="sm"
                              required
                              label={`Broker ${
                                selectedTab === CENTRO ? "Partner" : "Market"
                              }`}
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={6}>
                        {/* TODO(ds-button): link-button — DS Button type isn't polymorphic (component/to) */}
                        <MuiButton
                          fullWidth
                          variant="outlined"
                          color="brandPrimary"
                          component={RouterLink}
                          to={`/${ROUTE_NAMES.BROKERS}`}
                        >
                          Back
                        </MuiButton>
                      </Grid>
                      <Grid size={6}>
                        <Button
                          fullWidth
                          variant="primary"
                          type="submit"
                          disabled={!broker || addBrokerMutation.isPending}
                        >
                          {addBrokerMutation.isPending
                            ? "Adding..."
                            : "Add Broker"}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </form>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default BrokerPreRegistration;
